import {
  Button,
  formatNumber,
  IconButton,
  MultiSelectMenu,
  SearchBox,
  Skeleton,
} from '@platform/design-system';
import { useAuth } from '@platform/auth-client';
import type { TypedClient } from '@platform/openapi';
import { AlertTriangle, SlidersHorizontal, Tags, Warehouse } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PRODUCTS_DEFAULTS } from '../../app/products-search.ts';
import { useProductList } from '../catalog/list/useProductList.ts';
import { indexOptions } from '../catalog/options.ts';
import type { Product } from '../catalog/product.ts';
import { useOptions } from '../catalog/useCatalogQueries.ts';
import type { LabelSelection } from '../labels/labels.ts';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';
import { INVENTORY_WRITE_ROLE, type Location } from './inventory.ts';
import { gridRowsOf, levelsShown, needsRestock, type GridRow } from './stock-grid.ts';
import { StockLegend, StockMatrix } from './StockMatrix.tsx';
import { useStockOverview } from '../dashboard/stock.ts';
import { AdjustStockSheet } from './AdjustStockSheet.tsx';
import { productNameOf, variantRows } from '../catalog/product-stock.ts';
import { shownDepotIdsOf, stockedDepotIdsOf, useLevelColumns } from './level-columns.ts';
import styles from './Inventory.module.css';

const ONE_LABEL = 1;

export type InventoryLevelsProps = {
  api: TypedClient;
  locations: readonly Location[];
  onPrintLabels: (selection: LabelSelection) => void;
};

export const InventoryLevels = ({ api, locations, onPrintLabels }: InventoryLevelsProps) => {
  const { t, locale, describeError } = usePageContext();
  const canAdjust = useAuth().hasRole(INVENTORY_WRITE_ROLE);
  const [search, setSearch] = useState<string | null>(null);
  const [restockOnly, setRestockOnly] = useState(false);
  const [adjusting, setAdjusting] = useState<{ product: Product; variantId: string } | null>(null);
  const overview = useStockOverview(api, null).data;
  const trackedVariants = overview?.totals.variants;
  const depotUnits = overview?.depots ?? [];
  const { chosen, choose } = useLevelColumns();
  const list = useProductList(api, { ...PRODUCTS_DEFAULTS, search });
  const options = useOptions(api);
  const optionIndex = useMemo(() => indexOptions(options.data?.items ?? [], locale), [options.data, locale]);

  const locationIds = shownDepotIdsOf({ locations, units: depotUnits, chosen });
  const shownLocations = locations.filter((location) => locationIds.includes(location.id));
  const depotSummary =
    locationIds.length === locations.length
      ? t('inventory.depotColumnsAll', { count: locations.length })
      : shownLocations.length === 1
        ? (shownLocations[0]?.name ?? '')
        : t('inventory.depotColumnsSome', {
            shown: formatNumber(locationIds.length, locale),
            total: formatNumber(locations.length, locale),
          });
  const allRows = gridRowsOf(list.products, optionIndex, locale);
  const rows = restockOnly ? allRows.filter((row) => needsRestock(row, list.levels, locationIds)) : allRows;
  const productOf = (row: GridRow) => list.products.find((product) => product.id === row.productId);

  const actionsOf = (row: GridRow) => {
    const product = productOf(row);
    if (product === undefined) return null;
    const name = `${row.label} ${row.detail ?? ''}`.trim();
    return (
      <>
        {canAdjust ? (
          <IconButton
            size="sm"
            label={t('productList.adjustRow', { name })}
            icon={<SlidersHorizontal size={16} aria-hidden="true" />}
            onClick={() => {
              setAdjusting({ product, variantId: row.variantId });
            }}
          />
        ) : null}
        <IconButton
          size="sm"
          label={t('labels.printFor', { name })}
          icon={<Tags size={16} aria-hidden="true" />}
          onClick={() => {
            onPrintLabels({
              productIds: [product.id],
              focus: { variantId: row.variantId, quantity: ONE_LABEL },
            });
          }}
        />
      </>
    );
  };

  const body = () => {
    if (list.query.isError)
      return (
        <LoadFailed description={describeError(list.query.error)} onRetry={() => void list.query.refetch()} />
      );
    if (list.query.isPending) return <Skeleton height="16rem" />;
    return (
      <StockMatrix
        rows={rows}
        locations={shownLocations}
        levels={levelsShown(list.levels, rows, locationIds)}
        showThreshold
        actionsOf={actionsOf}
        totalLabel={t('stockMatrix.shownTotal')}
        showLegend={false}
      />
    );
  };

  return (
    <div>
      <div className={styles['toolbar']}>
        <SearchBox
          label={t('inventory.levelsSearchLabel')}
          placeholder={t('inventory.levelsSearch')}
          onSearch={(text) => {
            setSearch(text.trim() === '' ? null : text.trim());
          }}
        />
        <MultiSelectMenu
          label={t('inventory.depotColumns')}
          summary={depotSummary}
          icon={<Warehouse size={16} aria-hidden="true" />}
          value={locationIds}
          options={locations.map((location) => ({
            value: location.id,
            label: location.name,
            detail: formatNumber(
              depotUnits.find((depot) => depot.locationId === location.id)?.onHand ?? 0,
              locale,
            ),
          }))}
          presets={[
            { label: t('inventory.depotColumnsStocked'), values: stockedDepotIdsOf(locations, depotUnits) },
            { label: t('inventory.allDepots'), values: locations.map((location) => location.id) },
          ]}
          onChange={choose}
        />
        <button
          type="button"
          className={styles['restockToggle']}
          aria-pressed={restockOnly}
          onClick={() => {
            setRestockOnly((current) => !current);
          }}
        >
          <AlertTriangle size={16} aria-hidden="true" />
          {t('inventory.restockOnly')}
        </button>
      </div>
      <div className={styles['matrix']}>{body()}</div>
      {adjusting === null ? null : (
        <AdjustStockSheet
          api={api}
          subtitle={productNameOf(adjusting.product, locale)}
          rows={variantRows(adjusting.product, optionIndex).filter(
            (row) => row.variantId === adjusting.variantId,
          )}
          onClose={() => {
            setAdjusting(null);
          }}
        />
      )}
      {list.products.length === 0 ? null : (
        <div className={styles['footer']}>
          <StockLegend>
            <span className={styles['shown']}>
              {t('inventory.levelsShown', {
                count: rows.length,
                shown: formatNumber(rows.length, locale),
                total: formatNumber(trackedVariants ?? rows.length, locale),
              })}
            </span>
          </StockLegend>
          {list.hasMore ? (
            <Button
              variant="outline"
              size="sm"
              disabled={list.loadingMore}
              onClick={list.loadMore}
              className={styles['more']}
            >
              {list.loadingMore ? t('common.loadingMore') : t('common.loadMore')}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
};
